import React, {
  useMemo,
  useState,
  useRef,
  useEffect,
  useCallback,
} from "react";
import { Platform, Dimensions } from "react-native";
import {
  Box,
  VStack,
  HStack,
  Text,
  Input,
  Pressable,
  Image,
  Checkbox,
  Center,
  useToast,
  ScrollView,
  Stack,
  Button,
  IconButton,
  ChevronUpIcon,
  Spinner,
  Modal,
} from "native-base";
import { LinearGradient } from "expo-linear-gradient";
import { GlobalStyles } from "../../constants/styles";
import { GlobalConstants } from "../../constants/constants";
import { useSelector } from "react-redux";
import axios from "axios";
import Toast from "../../components/Toast";
import { DatePickerModal } from "react-native-paper-dates";
import { SafeAreaProvider } from "react-native-safe-area-context";

const COUPON_API_BASE_URL = `${GlobalConstants.COUPONAPIBASEURL}`.replace(/\/$/, "") + "/api/coupon";

const cardWidthProp = ["92%", "86%", "640px"];

const formatDateMMDDYYYY = (d) => {
  if (!d) return "";
  const dd = d.getDate();
  const mm = d.getMonth() + 1;
  const yyyy = d.getFullYear();
  const pad = (n) => (n < 10 ? "0" + n : String(n));
  return `${pad(mm)}/${pad(dd)}/${yyyy}`;
};

const formatDateToISO = (dateString) => {
  if (!dateString) return "";
  const parts = dateString.split("/");
  if (parts.length === 3) {
    const [mm, dd, yyyy] = parts;
    return new Date(`${yyyy}-${mm}-${dd}T23:59:59.999Z`).toISOString();
  }
  return "";
};

const getCouponTypeLabel = (couponType) => {
  switch (couponType) {
    case "percentageDiscount":
      return "Percentage Discount";
    case "fixedDiscount":
      return "Fixed Amount Discount";
    case "other":
      return "Buy One Get One";
    default:
      return couponType;
  }
};

const GradientButton = ({
  children,
  onPress,
  width = "auto",
  px = 12,
  py = 12,
  isLoading = false,
}) => {
  return (
    <Box
      borderRadius={8}
      overflow="hidden"
      borderWidth={1}
      borderColor={GlobalStyles.colors.black}
      bg="transparent"
      width={width}
      alignSelf="center"
    >
      <LinearGradient
        colors={[
          GlobalStyles.colors.gradientBlueStart,
          GlobalStyles.colors.gradientBlueEnd,
        ]}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 0 }}
        style={{
          width: "100%",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Pressable
          onPress={onPress}
          disabled={isLoading}
          style={{
            width: "100%",
            paddingVertical: py,
            paddingHorizontal: px,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {isLoading ? (
            <Spinner color={GlobalStyles.colors.mustard} size="sm" />
          ) : (
            <Text
              color={GlobalStyles.colors.mustard}
              fontWeight="400"
              fontSize={["13px", "14px", "14px"]}
              fontFamily={GlobalStyles.text.fontFamily}
            >
              {children}
            </Text>
          )}
        </Pressable>
      </LinearGradient>
    </Box>
  );
};

const CouponGenerated = ({ coupon }) => {
  if (!coupon) return null;

  return (
    <Center py={0}>
      <Box
        w={cardWidthProp}
        bg="transparent"
        borderRadius={12}
        p={[4, 5, 6]}
        mt={0}
      >
        <VStack space={[4, 5, 6]} alignItems="center" w="100%">
          <Text
            fontSize={["16px", "17px", "18px"]}
            fontWeight="500"
            color={GlobalStyles.colors.blue}
            fontFamily={GlobalStyles.text.fontFamily}
            textAlign="center"
          >
            Coupon Generated
          </Text>

          <Box
            bg={GlobalStyles.colors.mustardLightBg || "#FFC0000D"}
            borderRadius={8}
            px={8}
            py={8}
            alignItems="center"
            w="100%"
          >
            {coupon.qr && (
              <Image
                source={{ uri: coupon.qr }}
                alt="QR"
                style={{ width: 120, height: 120, marginBottom: 24 }}
                resizeMode="contain"
              />
            )}
            <VStack space={2} alignItems="flex-start" w="100%">
              {/* ADDED: Coupon ID row */}
              <HStack justifyContent="space-between" w="100%">
                <Text
                  fontWeight="400"
                  fontSize={["13px", "14px", "15px"]}
                  color={GlobalStyles.colors.black}
                  fontFamily={GlobalStyles.text.fontFamily}
                >
                  Coupon ID:
                </Text>
                <Text
                  color={GlobalStyles.colors.black}
                  fontSize={["13px", "14px", "15px"]}
                  fontFamily={GlobalStyles.text.fontFamily}
                >
                  {coupon.id}
                </Text>
              </HStack>
              <HStack justifyContent="space-between" w="100%">
                <Text
                  fontWeight="400"
                  fontSize={["13px", "14px", "15px"]}
                  color={GlobalStyles.colors.black}
                  fontFamily={GlobalStyles.text.fontFamily}
                >
                  Coupon Name:
                </Text>
                <Text
                  color={GlobalStyles.colors.black}
                  fontSize={["13px", "14px", "15px"]}
                  fontFamily={GlobalStyles.text.fontFamily}
                >
                  {coupon.name}
                </Text>
              </HStack>
              <HStack justifyContent="space-between" w="100%">
                <Text
                  fontWeight="400"
                  fontSize={["13px", "14px", "15px"]}
                  color={GlobalStyles.colors.black}
                  fontFamily={GlobalStyles.text.fontFamily}
                >
                  Coupon ID:
                </Text>
                <Text
                  color={GlobalStyles.colors.black}
                  fontSize={["13px", "14px", "15px"]}
                  fontFamily={GlobalStyles.text.fontFamily}
                >
                  {coupon.coupon_code || coupon.id}
                </Text>
              </HStack>
              <HStack justifyContent="space-between" w="100%">
                <Text
                  fontWeight="400"
                  fontSize={["13px", "14px", "15px"]}
                  color={GlobalStyles.colors.black}
                  fontFamily={GlobalStyles.text.fontFamily}
                >
                  Company created:
                </Text>
                <Text
                  color={GlobalStyles.colors.black}
                  fontSize={["13px", "14px", "15px"]}
                  fontFamily={GlobalStyles.text.fontFamily}
                >
                  {coupon.company}
                </Text>
              </HStack>
              <HStack justifyContent="space-between" w="100%">
                <Text
                  fontWeight="400"
                  fontSize={["13px", "14px", "15px"]}
                  color={GlobalStyles.colors.black}
                  fontFamily={GlobalStyles.text.fontFamily}
                >
                  Date created:
                </Text>
                <Text
                  color={GlobalStyles.colors.black}
                  fontSize={["13px", "14px", "15px"]}
                  fontFamily={GlobalStyles.text.fontFamily}
                >
                  {coupon.date}
                </Text>
              </HStack>
            </VStack>
          </Box>
        </VStack>
      </Box>
    </Center>
  );
};

const couponTypes = [
  { label: "Percentage Discount", value: "percentageDiscount" },
  { label: "Fixed amount Discount", value: "fixedDiscount" },
  { label: "Buy ( ) get ( )", value: "other" },
];

const EditCouponModal = ({ coupon, onClose, onUpdated, userToken }) => {
  const toast = useToast();
  const [couponName, setCouponName] = useState(coupon?.name || "");
  const [couponType, setCouponType] = useState(
    coupon?.coupon_type || "percentageDiscount",
  );
  const [couponCount, setCouponCount] = useState(
    coupon?.total_available || 5,
  );
  const [expiryDate, setExpiryDate] = useState(() => {
    if (!coupon?.expiry_date) return "";
    return formatDateMMDDYYYY(new Date(coupon.expiry_date));
  });
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const updateCoupon = async () => {
    if (!couponName.trim()) {
      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.red}
            message="Please enter a coupon name"
          />
        ),
        placement: "top",
        duration: 3000,
      });
      return;
    }
    if (!expiryDate) {
      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.red}
            message="Please select an expiry date"
          />
        ),
        placement: "top",
        duration: 3000,
      });
      return;
    }
    try {
      setIsLoading(true);
      const payload = {
        name: couponName.trim(),
        company_name: coupon.company_name,
        coupon_type: couponType,
        total_available: couponCount,
        expiry_date: formatDateToISO(expiryDate),
      };
      const resp = await axios.put(
        `${COUPON_API_BASE_URL}/${coupon.id}`,
        payload,
        {
          headers: { Authorization: `Bearer ${userToken}` },
          timeout: 10000,
        },
      );
      if (resp.data?.coupon) {
        toast.show({
          render: () => (
            <Toast
              color={GlobalStyles.colors.green}
              message="Coupon updated successfully!"
            />
          ),
          placement: "top",
          duration: 3000,
        });
        onUpdated(resp.data.coupon);
        onClose();
      }
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        error.response?.data?.errors?.[0]?.msg ||
        "Failed to update coupon";
      toast.show({
        render: () => (
          <Toast color={GlobalStyles.colors.red} message={msg} />
        ),
        placement: "top",
        duration: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <SafeAreaProvider>
      <Modal isOpen onClose={onClose} size="lg">
        <Modal.Content borderRadius={12} maxW="440px">
          <Modal.CloseButton />
          <Modal.Header>
            <Text
              fontSize={["14px", "15px", "16px"]}
              fontWeight="500"
              color={GlobalStyles.colors.blue}
              fontFamily={GlobalStyles.text.fontFamily}
            >
              Edit Coupon
            </Text>
          </Modal.Header>
          <Modal.Body>
            <VStack space={4}>
              <VStack space={1}>
                <Text
                  fontWeight="500"
                  fontSize={["14px", "15px", "16px"]}
                  color={GlobalStyles.colors.blue}
                  fontFamily={GlobalStyles.text.fontFamily}
                >
                  Coupon Name *
                </Text>
                <Input
                  value={couponName}
                  onChangeText={setCouponName}
                  borderColor={GlobalStyles.colors.mustard}
                  borderWidth={2}
                  borderRadius={6}
                  bg={GlobalStyles.colors.white}
                  fontFamily={GlobalStyles.text.fontFamily}
                  fontSize={["sm", "md", "md"]}
                  _focus={{
                    borderColor: GlobalStyles.colors.mustard,
                    bg: GlobalStyles.colors.white,
                  }}
                  py={3}
                />
              </VStack>

              <VStack space={1}>
                <Text
                  fontWeight="500"
                  fontSize={["14px", "15px", "16px"]}
                  color={GlobalStyles.colors.blue}
                  fontFamily={GlobalStyles.text.fontFamily}
                >
                  Coupon Type *
                </Text>
                <Box
                  bg={GlobalStyles.colors.mustardLightBg || "#FFC0000D"}
                  borderRadius={8}
                  px={4}
                  py={3}
                >
                  <VStack space={3}>
                    {couponTypes.map((type) => (
                      <Pressable
                        key={type.value}
                        onPress={() => setCouponType(type.value)}
                      >
                        <HStack alignItems="center">
                          <Box
                            borderWidth={2}
                            borderColor={GlobalStyles.colors.blue}
                            borderRadius={999}
                            width={5}
                            height={5}
                            alignItems="center"
                            justifyContent="center"
                            bg={
                              couponType === type.value
                                ? GlobalStyles.colors.blue
                                : GlobalStyles.colors.white
                            }
                            mr={3}
                          >
                            {couponType === type.value && (
                              <Box
                                width={2.5}
                                height={2.5}
                                borderRadius={999}
                                bg={GlobalStyles.colors.white}
                              />
                            )}
                          </Box>
                          <Text
                            fontFamily={GlobalStyles.text.fontFamily}
                            color={GlobalStyles.colors.black}
                            fontSize={["13px", "14px", "14px"]}
                          >
                            {type.label}
                          </Text>
                        </HStack>
                      </Pressable>
                    ))}
                  </VStack>
                </Box>
              </VStack>

              <HStack alignItems="center" justifyContent="space-between">
                <Text
                  fontWeight="500"
                  fontSize={["14px", "15px", "16px"]}
                  color={GlobalStyles.colors.blue}
                  fontFamily={GlobalStyles.text.fontFamily}
                >
                  Total Available
                </Text>
                <HStack alignItems="center" space={3}>
                  <Pressable
                    onPress={() => setCouponCount((c) => Math.max(1, c - 1))}
                  >
                    <Text
                      fontSize="20px"
                      color={GlobalStyles.colors.blue}
                      fontWeight="700"
                    >
                      ‹
                    </Text>
                  </Pressable>
                  <Box
                    w={12}
                    h={8}
                    borderColor={GlobalStyles.colors.blue}
                    borderWidth={1}
                    borderRadius={6}
                    bg={GlobalStyles.colors.white}
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Text
                      fontFamily={GlobalStyles.text.fontFamily}
                      fontSize={["sm", "md", "md"]}
                      fontWeight="700"
                      color={GlobalStyles.colors.black}
                    >
                      {couponCount}
                    </Text>
                  </Box>
                  <Pressable onPress={() => setCouponCount((c) => c + 1)}>
                    <Text
                      fontSize="20px"
                      color={GlobalStyles.colors.blue}
                      fontWeight="700"
                    >
                      ›
                    </Text>
                  </Pressable>
                </HStack>
              </HStack>

              <VStack space={1}>
                <Text
                  fontWeight="500"
                  fontSize={["14px", "15px", "16px"]}
                  color={GlobalStyles.colors.blue}
                  fontFamily={GlobalStyles.text.fontFamily}
                >
                  Expiry Date *
                </Text>
                <Input
                  borderColor={GlobalStyles.colors.mustard}
                  borderWidth={1}
                  borderRadius={6}
                  bg={GlobalStyles.colors.white}
                  placeholder="MM/DD/YYYY"
                  value={expiryDate}
                  isReadOnly
                  InputRightElement={
                    <Pressable onPress={() => setShowDatePicker(true)}>
                      <Box mr={2}>
                        <Image
                          source={require("../../../assets/calendericon.png")}
                          alt="Calendar"
                          style={{ width: 18, height: 18 }}
                        />
                      </Box>
                    </Pressable>
                  }
                  _focus={{ borderColor: GlobalStyles.colors.mustard }}
                  fontFamily={GlobalStyles.text.fontFamily}
                  fontSize={["sm", "md", "md"]}
                  py={3}
                />
                <DatePickerModal
                  locale="en"
                  mode="single"
                  visible={showDatePicker}
                  onDismiss={() => setShowDatePicker(false)}
                  date={expiryDate ? new Date(expiryDate) : undefined}
                  onConfirm={(params) => {
                    if (params.date)
                      setExpiryDate(formatDateMMDDYYYY(params.date));
                    setShowDatePicker(false);
                  }}
                  validRange={{
                    startDate: new Date(),
                    endDate: new Date(2100, 11, 31),
                  }}
                />
              </VStack>
            </VStack>
          </Modal.Body>
          <Modal.Footer>
            <HStack space={3} justifyContent="flex-end" w="100%">
              <Pressable onPress={onClose}>
                <Box
                  borderRadius={8}
                  borderWidth={2}
                  borderColor={GlobalStyles.colors.blue}
                  px={5}
                  py={2}
                >
                  <Text
                    color={GlobalStyles.colors.blue}
                    fontFamily={GlobalStyles.text.fontFamily}
                    fontSize={["13px", "14px", "14px"]}
                  >
                    Cancel
                  </Text>
                </Box>
              </Pressable>
              <GradientButton
                onPress={updateCoupon}
                width="130px"
                py={10}
                isLoading={isLoading}
              >
                Save Changes
              </GradientButton>
            </HStack>
          </Modal.Footer>
        </Modal.Content>
      </Modal>
    </SafeAreaProvider>
  );
};

export function CouponList({ coupons = [], onDeleteSuccess, onUpdateSuccess }) {
  const toast = useToast();
  const user = useSelector((state) => state.user?.user);
  const [values, setValues] = useState([]);
  const [deletingId, setDeletingId] = useState(null);
  const [editingCoupon, setEditingCoupon] = useState(null);

  useEffect(() => {
    if (user?.token) {
      axios.defaults.headers.common = {
        Authorization: `Bearer ${user.token}`,
      };
    }
  }, [user]);

  const items = useMemo(
    () =>
      (coupons || []).map((c) => ({
        id: String(c.id),
        label: c.name || c.label,
        originalId: c.id,
        fullCoupon: c,
      })),
    [coupons],
  );

  const handleDelete = async (couponId) => {
    try {
      setDeletingId(couponId);

      await axios.delete(`${COUPON_API_BASE_URL}/${couponId}`, {
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
        timeout: 3000,
      });

      if (onDeleteSuccess) {
        onDeleteSuccess(couponId);
      }

      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.green}
            message="Coupon deleted successfully!"
          />
        ),
        placement: "top",
        duration: 3000,
      });
    } catch (error) {
      console.error("Delete error:", error);

      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        (error.code === "ECONNABORTED"
          ? "Request timeout. Please try again."
          : "Failed to delete coupon!");

      toast.show({
        render: () => (
          <Toast color={GlobalStyles.colors.red} message={errorMessage} />
        ),
        placement: "top",
        duration: 3000,
      });
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Center py={0}>
      <Box
        w={cardWidthProp}
        bg="transparent"
        borderRadius={12}
        p={[4, 5, 6]}
        mt={0}
      >
        <VStack space={[4, 5, 5]} alignItems="flex-start" w="100%">
          <Text
            fontSize={["14px", "15px", "16px"]}
            fontWeight="500"
            color={GlobalStyles.colors.blue}
            fontFamily={GlobalStyles.text.fontFamily}
          >
            Select Coupon from created list
          </Text>

          {items.length === 0 ? (
            <Center py={6} w="100%">
              <Text color="gray.500" fontFamily={GlobalStyles.text.fontFamily}>
                No coupons available
              </Text>
            </Center>
          ) : (
            <VStack space={4} alignItems="flex-start" w="100%">
              {items.map((item) => (
                <HStack
                  key={item.id}
                  w="100%"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Checkbox
                    value={item.id}
                    bg={GlobalStyles.colors.white}
                    borderColor={GlobalStyles.colors.blue}
                    borderWidth={2}
                    borderRadius={2}
                    _icon={{ color: GlobalStyles.colors.blue }}
                    _checked={{
                      bg: GlobalStyles.colors.white,
                      borderColor: GlobalStyles.colors.blue,
                      _icon: { color: GlobalStyles.colors.blue },
                    }}
                    _pressed={{ bg: GlobalStyles.colors.white }}
                    _hover={{ bg: GlobalStyles.colors.white }}
                    flex={1}
                  >
                    <Text
                      color={GlobalStyles.colors.black}
                      ml={2}
                      fontFamily={GlobalStyles.text.fontFamily}
                    >
                      {item.label}
                    </Text>
                  </Checkbox>

                  <HStack space={2} ml={4} alignItems="center">
                    <Pressable onPress={() => setEditingCoupon(item.fullCoupon)}>
                      <Box
                        borderRadius={8}
                        overflow="hidden"
                        borderWidth={1}
                        borderColor={GlobalStyles.colors.black}
                      >
                        <LinearGradient
                          colors={[
                            GlobalStyles.colors.gradientBlueStart,
                            GlobalStyles.colors.gradientBlueEnd,
                          ]}
                          start={{ x: 0, y: 0 }}
                          end={{ x: 1, y: 0 }}
                          style={{
                            paddingHorizontal: 12,
                            paddingVertical: 8,
                          }}
                        >
                          <Text
                            color={GlobalStyles.colors.mustard}
                            fontWeight="700"
                            fontSize={["13px", "14px", "14px"]}
                            letterSpacing={0.2}
                            fontFamily={GlobalStyles.text.fontFamily}
                          >
                            Edit
                          </Text>
                        </LinearGradient>
                      </Box>
                    </Pressable>

                    <Box>
                      {deletingId === item.originalId ? (
                        <Spinner color={GlobalStyles.colors.blue} size="sm" />
                      ) : (
                        <Pressable
                          onPress={() => handleDelete(item.originalId)}
                        >
                          <Box
                            borderRadius={8}
                            overflow="hidden"
                            borderWidth={1}
                            borderColor={GlobalStyles.colors.black}
                          >
                            <LinearGradient
                              colors={[
                                GlobalStyles.colors.gradientBlueStart,
                                GlobalStyles.colors.gradientBlueEnd,
                              ]}
                              start={{ x: 0, y: 0 }}
                              end={{ x: 1, y: 0 }}
                              style={{
                                paddingHorizontal: 12,
                                paddingVertical: 8,
                              }}
                            >
                              <Text
                                color={GlobalStyles.colors.mustard}
                                fontWeight="700"
                                fontSize={["13px", "14px", "14px"]}
                                letterSpacing={0.2}
                                fontFamily={GlobalStyles.text.fontFamily}
                              >
                                Delete
                              </Text>
                            </LinearGradient>
                          </Box>
                        </Pressable>
                      )}
                    </Box>
                  </HStack>
                </HStack>
              ))}
            </VStack>
          )}
        </VStack>
      </Box>

      {editingCoupon && (
        <EditCouponModal
          coupon={editingCoupon}
          userToken={user?.token}
          onClose={() => setEditingCoupon(null)}
          onUpdated={(updated) => {
            if (onUpdateSuccess) onUpdateSuccess(updated);
            setEditingCoupon(null);
          }}
        />
      )}
    </Center>
  );
}

export function CouponCreate({ onGenerated }) {
  const toast = useToast();
  const user = useSelector((state) => state.user?.user);

  const [couponName, setCouponName] = useState("");
  const [couponType, setCouponType] = useState("percentageDiscount");
  const [couponCount, setCouponCount] = useState(5);
  const [expiryDate, setExpiryDate] = useState("");
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (user?.token) {
      axios.defaults.headers.common = {
        Authorization: `Bearer ${user.token}`,
      };
    }
  }, [user]);

  const createCoupon = useCallback(async () => {
    if (!couponName.trim()) {
      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.red}
            message="Please enter a coupon name"
          />
        ),
        placement: "top",
        duration: 3000,
      });
      return;
    }

    if (couponCount < 1) {
      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.red}
            message="Coupon count must be at least 1"
          />
        ),
        placement: "top",
        duration: 3000,
      });
      return;
    }

    if (!expiryDate) {
      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.red}
            message="Please select an expiry date"
          />
        ),
        placement: "top",
        duration: 3000,
      });
      return;
    }

    try {
      setIsLoading(true);

      const expiryISO = formatDateToISO(expiryDate);

      const payload = {
        name: couponName.trim(),
        company_name:
          user?.business_name ||
          user?.name ||
          user?.email?.split("@")[0] ||
          "Unknown Company",
        coupon_type: couponType,
        total_available: couponCount,
        expiry_date: expiryISO,
      };

      const axiosInstance = axios.create({
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
        timeout: 10000,
      });

      const resp = await axiosInstance.post(COUPON_API_BASE_URL, payload);

      if (resp.data && resp.data.newCoupon) {
        const createdCoupon = resp.data.newCoupon;

        const couponData = {
          id: createdCoupon.id,
          name: createdCoupon.name,
          label: createdCoupon.name,
          coupon_code: createdCoupon.coupon_code,
          company: createdCoupon.company_name,
          date: new Date(createdCoupon.created_at).toLocaleDateString(),
          qr: createdCoupon.qr_code || createdCoupon.qrCode,
          fullData: createdCoupon,
        };

        toast.show({
          render: () => (
            <Toast
              color={GlobalStyles.colors.green}
              message="Coupon created successfully!"
            />
          ),
          placement: "top",
          duration: 3000,
        });

        if (onGenerated) {
          onGenerated(couponData);
        }

        setCouponName("");
        setCouponType("percentageDiscount");
        setCouponCount(5);
        setExpiryDate("");
      }
    } catch (error) {
      let errorMsg = "Failed to create coupon!";

      if (error.response?.data?.errors) {
        errorMsg = error.response.data.errors[0]?.msg || errorMsg;
      } else if (error.response?.data?.message) {
        errorMsg = error.response.data.message;
      } else if (error.code === "ECONNABORTED") {
        errorMsg = "Request timeout.Check your connection.";
      } else if (!error.response && !error.request) {
        errorMsg = "Network error.Check your connection.";
      } else if (error.code === "ERR_NETWORK") {
        errorMsg = "Can't connect to server.";
      }

      toast.show({
        render: () => (
          <Toast color={GlobalStyles.colors.red} message={errorMsg} />
        ),
        placement: "top",
        duration: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  }, [
    couponName,
    couponType,
    couponCount,
    expiryDate,
    user,
    toast,
    onGenerated,
  ]);

  return (
    <SafeAreaProvider>
      <Center py={0}>
        <Box
          w={cardWidthProp}
          bg="transparent"
          borderRadius={12}
          p={[4, 5, 6]}
          mt={0}
        >
          <VStack space={[4, 5, 6]} alignItems="flex-start" w="100%">
            <Text
              fontSize={["18px", "20px", "22px"]}
              fontWeight="500"
              color={GlobalStyles.colors.blue}
              fontFamily={GlobalStyles.text.fontFamily}
              textAlign="center"
              w="100%"
            >
              Create Coupons
            </Text>

            <VStack space={[4, 5, 6]} alignItems="flex-start" w="100%">
              <VStack space={1} w="100%">
                <Text
                  fontWeight="500"
                  fontSize={["14px", "15px", "16px"]}
                  color={GlobalStyles.colors.blue}
                  fontFamily={GlobalStyles.text.fontFamily}
                >
                  Enter Coupon Name *
                </Text>
                <Input
                  value={couponName}
                  onChangeText={setCouponName}
                  borderColor={GlobalStyles.colors.mustard}
                  borderWidth={2}
                  borderRadius={6}
                  bg={GlobalStyles.colors.white}
                  fontFamily={GlobalStyles.text.fontFamily}
                  fontSize={["sm", "md", "md"]}
                  _focus={{
                    borderColor: GlobalStyles.colors.mustard,
                    bg: GlobalStyles.colors.white,
                  }}
                  py={3}
                  w="100%"
                  placeholder="Enter coupon name"
                />
                <Text
                  mt={1}
                  fontSize={["11px", "12px", "12px"]}
                  fontWeight="300"
                  lineHeight="20px"
                  letterSpacing="0.1px"
                  color={GlobalStyles.colors.black}
                  fontFamily={GlobalStyles.text.fontFamily}
                  style={{ fontStyle: "normal" }}
                >
                  Tip: If discount is on specific product, mention it in name
                </Text>
              </VStack>

              <VStack space={2} w="100%">
                <Box
                  bg={GlobalStyles.colors.mustardLightBg || "#FFC0000D"}
                  borderRadius={8}
                  px={4}
                  py={3}
                  w="100%"
                >
                  <Text
                    fontWeight="500"
                    fontSize={["14px", "15px", "16px"]}
                    color={GlobalStyles.colors.blue}
                    fontFamily={GlobalStyles.text.fontFamily}
                    mb={2}
                    textAlign="left"
                  >
                    Select Coupon Type *
                  </Text>
                  <VStack space={3} w="100%" alignItems="flex-end">
                    {couponTypes.map((type) => (
                      <Pressable
                        key={type.value}
                        onPress={() => setCouponType(type.value)}
                        style={{ width: "60%" }}
                      >
                        <HStack alignItems="center" justifyContent="flex-end">
                          <Box
                            borderWidth={2}
                            borderColor={GlobalStyles.colors.blue}
                            borderRadius={999}
                            width={5}
                            height={5}
                            alignItems="center"
                            justifyContent="center"
                            bg={
                              couponType === type.value
                                ? GlobalStyles.colors.blue
                                : GlobalStyles.colors.white
                            }
                            mr={3}
                          >
                            {couponType === type.value && (
                              <Box
                                width={2.5}
                                height={2.5}
                                borderRadius={999}
                                bg={GlobalStyles.colors.white}
                              />
                            )}
                          </Box>
                          <Text
                            fontFamily={GlobalStyles.text.fontFamily}
                            color={GlobalStyles.colors.black}
                            fontSize={["13px", "14px", "14px"]}
                            style={{ minWidth: 180, textAlign: "left" }}
                          >
                            {type.label}
                          </Text>
                        </HStack>
                      </Pressable>
                    ))}
                  </VStack>
                </Box>
              </VStack>

              <HStack
                alignItems="center"
                justifyContent="space-between"
                w="100%"
              >
                <Text
                  fontWeight="500"
                  color={GlobalStyles.colors.blue}
                  fontFamily={GlobalStyles.text.fontFamily}
                  fontSize={["14px", "15px", "16px"]}
                >
                  How many Coupons
                </Text>
                <HStack alignItems="center" space={3} pr="20%">
                  <Pressable
                    onPress={() => setCouponCount((c) => Math.max(1, c - 1))}
                  >
                    <Image
                      source={require("../../../assets/leftarrow.png")}
                      alt="left arrow"
                      width={5}
                      height={5}
                      resizeMode="contain"
                    />
                  </Pressable>
                  <Box
                    w={12}
                    h={8}
                    borderColor={GlobalStyles.colors.blue}
                    borderWidth={1}
                    borderRadius={6}
                    bg={GlobalStyles.colors.white}
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Text
                      fontFamily={GlobalStyles.text.fontFamily}
                      fontSize={["sm", "md", "md"]}
                      fontWeight="700"
                      color={GlobalStyles.colors.black}
                    >
                      {couponCount}
                    </Text>
                  </Box>
                  <Pressable onPress={() => setCouponCount((c) => c + 1)}>
                    <Image
                      source={require("../../../assets/rightarrow.png")}
                      alt="right arrow"
                      width={5}
                      height={5}
                      resizeMode="contain"
                    />
                  </Pressable>
                </HStack>
              </HStack>

              <Stack
                flexDirection={window?.innerWidth < 415 ? "column" : "row"}
                alignItems={window?.innerWidth < 415 ? "flex-start" : "center"}
                justifyContent="space-between"
                space={window?.innerWidth < 415 ? 2 : 0}
                w="100%"
              >
                <Text
                  fontWeight="500"
                  color={GlobalStyles.colors.blue}
                  fontFamily={GlobalStyles.text.fontFamily}
                  fontSize={["sm", "md", "md"]}
                >
                  Coupon Expiry Date *
                </Text>
                <Input
                  w={window?.innerWidth < 415 ? "100%" : ["60%", "60%", "60%"]}
                  borderColor={GlobalStyles.colors.mustard}
                  borderWidth={1}
                  borderRadius={6}
                  bg={GlobalStyles.colors.white}
                  placeholder="MM/DD/YYYY"
                  value={expiryDate}
                  isReadOnly
                  InputRightElement={
                    <Pressable onPress={() => setShowDatePicker(true)}>
                      <Box mr={2}>
                        <Image
                          source={require("../../../assets/calendericon.png")}
                          alt="Calendar"
                          style={{ width: 18, height: 18 }}
                        />
                      </Box>
                    </Pressable>
                  }
                  _focus={{ borderColor: GlobalStyles.colors.mustard }}
                  fontFamily={GlobalStyles.text.fontFamily}
                />
              </Stack>

              <DatePickerModal
                locale="en"
                mode="single"
                visible={showDatePicker}
                onDismiss={() => setShowDatePicker(false)}
                date={expiryDate ? new Date(expiryDate) : undefined}
                onConfirm={(params) => {
                  const { date } = params;
                  if (date) {
                    setExpiryDate(formatDateMMDDYYYY(date));
                  }
                  setShowDatePicker(false);
                }}
                validRange={{
                  startDate: new Date(),
                  endDate: new Date(2100, 11, 31),
                }}
              />

              <Box w="100%" display="flex" justifyContent="center">
                <GradientButton
                  onPress={createCoupon}
                  width="150px"
                  isLoading={isLoading}
                >
                  Generate Coupon
                </GradientButton>
              </Box>
            </VStack>
          </VStack>
        </Box>
      </Center>
    </SafeAreaProvider>
  );
}

export function CouponRedemption() {
  const toast = useToast();
  const user = useSelector((state) => state.user?.user);

  const [couponId, setCouponId] = useState("");
  const [couponDetails, setCouponDetails] = useState(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [isRedeeming, setIsRedeeming] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    if (user?.token) {
      axios.defaults.headers.common = {
        Authorization: `Bearer ${user.token}`,
      };
    }
  }, [user]);

  const fetchCouponDetails = async () => {
    if (!couponId.trim()) {
      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.red}
            message="Please enter a coupon ID"
          />
        ),
        placement: "top",
        duration: 3000,
      });
      return;
    }

    try {
      setIsLoadingDetails(true);

      const resp = await axios.get(`${COUPON_API_BASE_URL}/${couponId.trim()}`, {
        headers: { Authorization: `Bearer ${user.token}` },
        timeout: 8000,
      });

      if (resp.data && resp.data.coupon) {
        setCouponDetails(resp.data.coupon);
        setShowDetails(true);
      }
    } catch (error) {
      console.error("Fetch coupon details error:", error);

      const errorMessage =
        error.response?.status === 404
          ? "Coupon not found"
          : error.code === "ECONNABORTED"
          ? "Request timeout. Please try again."
          : error.response?.data?.message || "Failed to fetch coupon details";

      toast.show({
        render: () => (
          <Toast color={GlobalStyles.colors.red} message={errorMessage} />
        ),
        placement: "top",
        duration: 3000,
      });

      setCouponDetails(null);
      setShowDetails(false);
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const redeemCoupon = async () => {
    if (!couponDetails) return;

    try {
      setIsRedeeming(true);

      const resp = await axios.post(
        `${COUPON_API_BASE_URL}/redeem`,
        { coupon_code: couponDetails.coupon_code },
        {
          headers: { Authorization: `Bearer ${user.token}` },
          timeout: 8000,
        },
      );

      if (resp.data) {
        toast.show({
          render: () => (
            <Toast
              color={GlobalStyles.colors.green}
              message="Coupon redeemed successfully!"
            />
          ),
          placement: "top",
          duration: 3000,
        });

        setCouponId("");
        setCouponDetails(null);
        setShowDetails(false);
      }
    } catch (error) {
      console.error("Redeem coupon error:", error);

      const errorMessage =
        error.response?.data?.message ||
        (error.code === "ECONNABORTED"
          ? "Request timeout. Please try again."
          : "Failed to redeem coupon");

      toast.show({
        render: () => (
          <Toast color={GlobalStyles.colors.red} message={errorMessage} />
        ),
        placement: "top",
        duration: 3000,
      });
    } finally {
      setIsRedeeming(false);
    }
  };

  return (
    <Center py={0}>
      <Box
        w={cardWidthProp}
        bg="transparent"
        borderRadius={12}
        p={[4, 5, 6]}
        mt={0}
      >
        <VStack space={[4, 5, 6]} alignItems="flex-start" w="100%">
          <Text
            fontSize={["18px", "20px", "22px"]}
            fontWeight="500"
            color={GlobalStyles.colors.blue}
            fontFamily={GlobalStyles.text.fontFamily}
            textAlign="center"
            w="100%"
          >
            Redeem Coupon
          </Text>

          <VStack space={3} w="100%">
            <Text
              fontWeight="500"
              fontSize={["14px", "15px", "16px"]}
              color={GlobalStyles.colors.blue}
              fontFamily={GlobalStyles.text.fontFamily}
            >
              Enter Coupon ID *
            </Text>
            <Input
              value={couponId}
              onChangeText={setCouponId}
              borderColor={GlobalStyles.colors.mustard}
              borderWidth={2}
              borderRadius={6}
              bg={GlobalStyles.colors.white}
              fontFamily={GlobalStyles.text.fontFamily}
              fontSize={["sm", "md", "md"]}
              _focus={{
                borderColor: GlobalStyles.colors.mustard,
                bg: GlobalStyles.colors.white,
              }}
              py={3}
              w="100%"
              placeholder="Enter coupon ID from QR scan"
            />
            <Text
              mt={1}
              fontSize={["11px", "12px", "12px"]}
              fontWeight="300"
              lineHeight="20px"
              letterSpacing="0.1px"
              color={GlobalStyles.colors.black}
              fontFamily={GlobalStyles.text.fontFamily}
              style={{ fontStyle: "normal" }}
            >
              Scan the QR code to reveal the coupon ID
            </Text>
          </VStack>

          <Box w="100%" display="flex" justifyContent="center">
            <GradientButton
              onPress={fetchCouponDetails}
              width="180px"
              isLoading={isLoadingDetails}
            >
              Get Coupon Details
            </GradientButton>
          </Box>

          {showDetails && couponDetails && (
            <Box
              bg={GlobalStyles.colors.mustardLightBg || "#FFC0000D"}
              borderRadius={8}
              px={6}
              py={6}
              w="100%"
              mt={2}
            >
              <VStack space={3} alignItems="flex-start" w="100%">
                <Text
                  fontSize={["15px", "16px", "17px"]}
                  fontWeight="600"
                  color={GlobalStyles.colors.blue}
                  fontFamily={GlobalStyles.text.fontFamily}
                  mb={2}
                >
                  Coupon Information
                </Text>

                <HStack justifyContent="space-between" w="100%">
                  <Text
                    fontWeight="400"
                    fontSize={["13px", "14px", "15px"]}
                    color={GlobalStyles.colors.black}
                    fontFamily={GlobalStyles.text.fontFamily}
                  >
                    Coupon Name:
                  </Text>
                  <Text
                    color={GlobalStyles.colors.black}
                    fontSize={["13px", "14px", "15px"]}
                    fontFamily={GlobalStyles.text.fontFamily}
                    fontWeight="600"
                  >
                    {couponDetails.name}
                  </Text>
                </HStack>

                <HStack justifyContent="space-between" w="100%">
                  <Text
                    fontWeight="400"
                    fontSize={["13px", "14px", "15px"]}
                    color={GlobalStyles.colors.black}
                    fontFamily={GlobalStyles.text.fontFamily}
                  >
                    Discount Type:
                  </Text>
                  <Text
                    color={GlobalStyles.colors.black}
                    fontSize={["13px", "14px", "15px"]}
                    fontFamily={GlobalStyles.text.fontFamily}
                    fontWeight="600"
                  >
                    {getCouponTypeLabel(couponDetails.coupon_type)}
                  </Text>
                </HStack>

                {couponDetails.product && (
                  <HStack justifyContent="space-between" w="100%">
                    <Text
                      fontWeight="400"
                      fontSize={["13px", "14px", "15px"]}
                      color={GlobalStyles.colors.black}
                      fontFamily={GlobalStyles.text.fontFamily}
                    >
                      Product:
                    </Text>
                    <Text
                      color={GlobalStyles.colors.black}
                      fontSize={["13px", "14px", "15px"]}
                      fontFamily={GlobalStyles.text.fontFamily}
                      fontWeight="600"
                    >
                      {couponDetails.product}
                    </Text>
                  </HStack>
                )}

                {couponDetails.description && (
                  <VStack space={1} w="100%">
                    <Text
                      fontWeight="400"
                      fontSize={["13px", "14px", "15px"]}
                      color={GlobalStyles.colors.black}
                      fontFamily={GlobalStyles.text.fontFamily}
                    >
                      Description:
                    </Text>
                    <Text
                      color={GlobalStyles.colors.black}
                      fontSize={["13px", "14px", "15px"]}
                      fontFamily={GlobalStyles.text.fontFamily}
                    >
                      {couponDetails.description}
                    </Text>
                  </VStack>
                )}

                <HStack justifyContent="space-between" w="100%">
                  <Text
                    fontWeight="400"
                    fontSize={["13px", "14px", "15px"]}
                    color={GlobalStyles.colors.black}
                    fontFamily={GlobalStyles.text.fontFamily}
                  >
                    Status:
                  </Text>
                  <Text
                    color={
                      couponDetails.status === "AVAILABLE"
                        ? GlobalStyles.colors.green
                        : couponDetails.status === "CLAIMED"
                        ? GlobalStyles.colors.mustard
                        : GlobalStyles.colors.red
                    }
                    fontSize={["13px", "14px", "15px"]}
                    fontFamily={GlobalStyles.text.fontFamily}
                    fontWeight="600"
                  >
                    {couponDetails.status}
                  </Text>
                </HStack>

                <HStack justifyContent="space-between" w="100%">
                  <Text
                    fontWeight="400"
                    fontSize={["13px", "14px", "15px"]}
                    color={GlobalStyles.colors.black}
                    fontFamily={GlobalStyles.text.fontFamily}
                  >
                    Expiry Date:
                  </Text>
                  <Text
                    color={GlobalStyles.colors.black}
                    fontSize={["13px", "14px", "15px"]}
                    fontFamily={GlobalStyles.text.fontFamily}
                    fontWeight="600"
                  >
                    {new Date(couponDetails.expiry_date).toLocaleDateString()}
                  </Text>
                </HStack>
              </VStack>
            </Box>
          )}

          {showDetails && couponDetails && (
            <Box w="100%" display="flex" justifyContent="center" mt={2}>
              <GradientButton
                onPress={redeemCoupon}
                width="150px"
                isLoading={isRedeeming}
              >
                Redeem Coupon
              </GradientButton>
            </Box>
          )}
        </VStack>
      </Box>
    </Center>
  );
}

export function CouponAnalytics({ coupons = [] }) {
  const toast = useToast();
  const user = useSelector((state) => state.user?.user);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [stats, setStats] = useState(null);
  const [isLoadingStats, setIsLoadingStats] = useState(false);

  useEffect(() => {
    if (user?.token) {
      axios.defaults.headers.common = {
        Authorization: `Bearer ${user.token}`,
      };
    }
  }, [user]);

  const fetchBusinessStats = useCallback(async () => {
    if (!user?.business_name && !user?.name) return;

    try {
      setIsLoadingStats(true);

      const companyName = encodeURIComponent(
        user?.business_name ||
        user?.name ||
        user?.email?.split("@")[0] ||
        "Unknown",
      );

      const resp = await axios.get(
        `${COUPON_API_BASE_URL}/business/${companyName}/stats`,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
          timeout: 8000,
        },
      );

      if (resp.data.stats) {
        setStats(resp.data.stats);
      }
    } catch (error) {
      console.error("Fetch stats error:", error);

      const errorMessage =
        error.code === "ECONNABORTED"
          ? "Request timeout. Please try again."
          : "Failed to fetch Analytics";

      toast.show({
        render: () => (
          <Toast color={GlobalStyles.colors.red} message={errorMessage} />
        ),
        placement: "top",
        duration: 3000,
      });
    } finally {
      setIsLoadingStats(false);
    }
  }, [user, toast]);

  useEffect(() => {
    fetchBusinessStats();
  }, [fetchBusinessStats]);

  const LIST_MAX_W = 520;
  const PAGE_MAX_W = 720;
  const CHART_W = 580;
  const CHART_H = 260;
  const PAD_LEFT = 44;
  const PAD_RIGHT = 12;
  const PAD_BOTTOM = 8;
  const PAD_TOP = 8;
  const MAX_VAL = 120;
  const STEP = 20;

  const statsFor = (coupon) => {
    if (!coupon) return { created: 0, claimed: 0, used: 0 };

    return {
      created: coupon.total_available || 0,
      claimed: coupon.claimed_count || 0,
      used: coupon.redeemed_count || 0,
    };
  };

  const barAreaH = CHART_H - PAD_TOP - PAD_BOTTOM;
  const scaleY = (v) =>
    Math.max(0, Math.min(barAreaH, (v / MAX_VAL) * barAreaH));

  if (!selectedCoupon) {
    return (
      <Center py={0}>
        <Box
          w={cardWidthProp}
          bg="transparent"
          borderRadius={12}
          p={[4, 5, 6]}
          mt={0}
        >
          <Text
            fontSize={["18px", "20px", "22px"]}
            fontWeight="400"
            color={GlobalStyles.colors.blue}
            textAlign="center"
            mb={[4, 5, 6]}
            fontFamily={GlobalStyles.text.fontFamily}
          >
            Coupon Analytics
          </Text>

          {isLoadingStats && (
            <Center py={4}>
              <Spinner color={GlobalStyles.colors.blue} size="lg" />
            </Center>
          )}

          <VStack alignItems="flex-start" w="100%">
            <HStack w="100%" justifyContent="space-between" mb={3} px={1}>
              <Text
                fontWeight="500"
                fontSize={["13px", "14px", "14px"]}
                color={GlobalStyles.colors.blue}
                fontFamily={GlobalStyles.text.fontFamily}
              >
                Current Coupons
              </Text>
              <Text
                fontWeight="500"
                fontSize={["13px", "14px", "14px"]}
                color={GlobalStyles.colors.blue}
                fontFamily={GlobalStyles.text.fontFamily}
              >
                Stats
              </Text>
            </HStack>

            {coupons.length === 0 ? (
              <Center py={6} w="100%">
                <Text
                  color="gray.500"
                  fontFamily={GlobalStyles.text.fontFamily}
                >
                  No coupons available
                </Text>
              </Center>
            ) : (
              <VStack w="100%" space={4}>
                {coupons.map((item) => {
                  return (
                    <HStack
                      key={item.id}
                      w="100%"
                      justifyContent="space-between"
                      alignItems="center"
                      px={1}
                    >
                      <Text
                        fontSize={["13px", "14px", "14px"]}
                        color={GlobalStyles.colors.black}
                        fontFamily={GlobalStyles.text.fontFamily}
                      >
                        {item.name || item.label}
                      </Text>
                      <Pressable onPress={() => setSelectedCoupon(item)}>
                        <Image
                          source={require("../../../assets/couponAnalytics.png")}
                          alt="stats"
                          width={6}
                          height={6}
                          resizeMode="contain"
                        />
                      </Pressable>
                    </HStack>
                  );
                })}
              </VStack>
            )}
          </VStack>
        </Box>
      </Center>
    );
  }

  const s = statsFor(selectedCoupon);
  const createdH = scaleY(s.created);
  const claimedH = scaleY(s.claimed);
  const usedH = scaleY(s.used);

  const screenWidth = Dimensions.get("window").width;
  const chartWidth = Math.min(screenWidth - 100, CHART_W);
  const BAR_W = chartWidth < 400 ? 40 : chartWidth < 550 ? 50 : 60;
  const GROUP_W = BAR_W * 3;
  const drawW = chartWidth - PAD_LEFT - PAD_RIGHT;
  const baseX = PAD_LEFT + (drawW - GROUP_W) / 2;

  return (
    <Center py={0}>
      <Box
        w={cardWidthProp}
        bg="transparent"
        borderRadius={12}
        p={[4, 5, 6]}
        mt={0}
      >
        <Text
          fontSize={["18px", "20px", "22px"]}
          fontWeight="500"
          color={GlobalStyles.colors.blue}
          textAlign="center"
          mb={4}
          fontFamily={GlobalStyles.text.fontFamily}
        >
          Coupon Analytics
        </Text>

        <Box px={[2, 4, 4]} pt={3} pb={5}>
          <HStack w="100%" justifyContent="space-between" alignItems="center">
            <Text
              fontWeight="400"
              fontSize={["14px", "15px", "15px"]}
              color={GlobalStyles.colors.blue}
              fontFamily={GlobalStyles.text.fontFamily}
            >
              Coupons Usage
            </Text>
            <Pressable onPress={() => setSelectedCoupon(null)}>
              <Text
                color={GlobalStyles.colors.blue}
                fontWeight="400"
                fontSize={["13px", "14px", "15px"]}
                fontFamily={GlobalStyles.text.fontFamily}
              >
                Back
              </Text>
            </Pressable>
          </HStack>

          <Box height="12px" />

          <Box
            position="relative"
            width={`${chartWidth}px`}
            mx="auto"
            height={`${CHART_H}px`}
          >
            {Array.from({ length: MAX_VAL / STEP + 1 }).map((_, idx) => {
              const value = idx * STEP;
              const y =
                CHART_H -
                PAD_BOTTOM -
                (value / MAX_VAL) * (CHART_H - PAD_TOP - PAD_BOTTOM);
              return (
                <React.Fragment key={value}>
                  <Box
                    position="absolute"
                    left={`${PAD_LEFT}px`}
                    right={`${chartWidth - drawW - PAD_LEFT}px`}
                    top={`${y}px`}
                    borderTopWidth={1}
                    borderColor={GlobalStyles.colors.black}
                  />
                  <Box
                    position="absolute"
                    left="0px"
                    top={`${y - 8}px`}
                    width={`${PAD_LEFT - 6}px`}
                  >
                    <Text
                      fontSize={["10px", "11px", "11px"]}
                      color={GlobalStyles.colors.black}
                      textAlign="right"
                      fontFamily={GlobalStyles.text.fontFamily}
                    >
                      {value}
                    </Text>
                  </Box>
                </React.Fragment>
              );
            })}

            <HStack
              position="absolute"
              bottom={`${PAD_BOTTOM}px`}
              left="0"
              right="0"
              justifyContent="flex-start"
              alignItems="flex-end"
            >
              <Box
                position="absolute"
                left={`${baseX}px`}
                bg={GlobalStyles.colors.blue}
                w={`${BAR_W}px`}
                h={`${createdH}px`}
              />
              <Box
                position="absolute"
                left={`${baseX + BAR_W}px`}
                bg={GlobalStyles.colors.gradientBlueEnd}
                w={`${BAR_W}px`}
                h={`${claimedH}px`}
              />
              <Box
                position="absolute"
                left={`${baseX + BAR_W * 2}px`}
                bg={GlobalStyles.colors.mustard}
                w={`${BAR_W}px`}
                h={`${usedH}px`}
              />
            </HStack>
          </Box>

          <Text
            textAlign="center"
            mt={3}
            color={GlobalStyles.colors.black}
            fontFamily={GlobalStyles.text.fontFamily}
          >
            {selectedCoupon.name || selectedCoupon.label}
          </Text>

          <HStack justifyContent="center" space={10} mt={4}>
            <HStack space={2} alignItems="center">
              <Box w="12px" h="12px" bg={GlobalStyles.colors.blue} />
              <Text
                fontSize={["11px", "12px", "12px"]}
                fontFamily={GlobalStyles.text.fontFamily}
              >
                Created
              </Text>
            </HStack>
            <HStack space={2} alignItems="center">
              <Box w="12px" h="12px" bg={GlobalStyles.colors.gradientBlueEnd} />
              <Text
                fontSize={["11px", "12px", "12px"]}
                fontFamily={GlobalStyles.text.fontFamily}
              >
                Claimed
              </Text>
            </HStack>
            <HStack space={2} alignItems="center">
              <Box w="12px" h="12px" bg={GlobalStyles.colors.mustard} />
              <Text
                fontSize={["11px", "12px", "12px"]}
                fontFamily={GlobalStyles.text.fontFamily}
              >
                Used
              </Text>
            </HStack>
          </HStack>
        </Box>
      </Box>
    </Center>
  );
}

export default function CouponManager({ onBack }) {
  const toast = useToast();
  const user = useSelector((state) => state.user?.user);
  const [currentTab, setCurrentTab] = useState("create");
  const [couponsList, setCouponsList] = useState([]);
  const [isLoadingCoupons, setIsLoadingCoupons] = useState(false);
  const [formStep, setFormStep] = useState("form");
  const [newCoupon, setNewCoupon] = useState(null);

  const [createBtnColor, setCreateBtnColor] = useState(
    GlobalStyles.colors.mustard,
  );
  const [redeemBtnColor, setRedeemBtnColor] = useState(
    GlobalStyles.colors.blue,
  );
  const [listBtnColor, setListBtnColor] = useState(GlobalStyles.colors.blue);
  const [analyticsBtnColor, setAnalyticsBtnColor] = useState(
    GlobalStyles.colors.blue,
  );

  const scrollRef = useRef(null);
  const [showScrollBtn, setShowScrollBtn] = useState(false);

  useEffect(() => {
    if (user?.token) {
      axios.defaults.headers.common = {
        Authorization: `Bearer ${user.token}`,
      };
    }
  }, [user]);

  const fetchCoupons = useCallback(async () => {
    if (!user?.token) return;

    try {
      setIsLoadingCoupons(true);

      const resp = await axios.get(COUPON_API_BASE_URL, {
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
        timeout: 8000,
      });

      if (resp.data.coupons) {
        setCouponsList(resp.data.coupons);
      }
    } catch (error) {
      console.error("Fetch coupons error:", error);

      const errorMessage =
        error.code === "ECONNABORTED"
          ? "Request timeout.try again."
          : "Failed to fetch coupons";

      toast.show({
        render: () => (
          <Toast color={GlobalStyles.colors.red} message={errorMessage} />
        ),
        placement: "top",
        duration: 3000,
      });
    } finally {
      setIsLoadingCoupons(false);
    }
  }, [user?.token, toast]);

  useEffect(() => {
    if (user?.token && (currentTab === "list" || currentTab === "analytics")) {
      fetchCoupons();
    }
  }, [user?.token, currentTab, fetchCoupons]);

  const changeTab = (tabType) => {
    if (tabType === "create") {
      setCreateBtnColor(GlobalStyles.colors.mustard);
      setRedeemBtnColor(GlobalStyles.colors.blue);  
      setListBtnColor(GlobalStyles.colors.blue);
      setAnalyticsBtnColor(GlobalStyles.colors.blue);
      setCurrentTab("create");
      setFormStep("form");
    } else if (tabType === "redeem") {               
      setCreateBtnColor(GlobalStyles.colors.blue);
      setRedeemBtnColor(GlobalStyles.colors.mustard);
      setListBtnColor(GlobalStyles.colors.blue);
      setAnalyticsBtnColor(GlobalStyles.colors.blue);
      setCurrentTab("redeem");
    } else if (tabType === "list") {
      setCreateBtnColor(GlobalStyles.colors.blue);
      setRedeemBtnColor(GlobalStyles.colors.blue);   
      setListBtnColor(GlobalStyles.colors.mustard);
      setAnalyticsBtnColor(GlobalStyles.colors.blue);
      setCurrentTab("list");
    } else if (tabType === "analytics") {
      setCreateBtnColor(GlobalStyles.colors.blue);
      setRedeemBtnColor(GlobalStyles.colors.blue);   
      setListBtnColor(GlobalStyles.colors.blue);
      setAnalyticsBtnColor(GlobalStyles.colors.mustard);
      setCurrentTab("analytics");
    }
  };

  const addCoupon = (newCoupon) => {
    setCouponsList((prev) => [...prev, newCoupon]);
    setNewCoupon(newCoupon);
    setFormStep("generated");
    fetchCoupons();
  };

  const removeCoupon = (couponId) => {
    setCouponsList((prev) => prev.filter((c) => c.id !== couponId));
    fetchCoupons();
  };

  const updateCoupon = (updated) => {
    setCouponsList((prev) =>
      prev.map((c) => (c.id === updated.id ? { ...c, ...updated } : c)),
    );
  };

  const goToTop = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ y: 0, animated: true });
    }
  };

  const checkScroll = (event) => {
    const scrollY = event.nativeEvent.contentOffset.y;
    setShowScrollBtn(scrollY > 100);
  };

  return (
    <>
      <ScrollView
        ref={scrollRef}
        onScroll={checkScroll}
        scrollEventThrottle={16}
        flex={1}
      >
        <Center>
          <Box safeAreaTop />
          <Stack
            mt={[1, 2, 3]}
            w="100%"
            maxW={["92%", 600, 850]}
            alignItems="center"
            justifyContent="center"
          >
            {onBack && (
              <HStack w="100%" mb={[2, 3, 3]} px={[2, 4, 4]}>
                <Pressable onPress={onBack}>
                  <HStack alignItems="center" space={2}>
                    <Text
                      color={GlobalStyles.colors.blue}
                      fontWeight="600"
                      fontSize={["14px", "15px", "16px"]}
                      fontFamily={GlobalStyles.text.fontFamily}
                    >
                      ← Back to Services
                    </Text>
                  </HStack>
                </Pressable>
              </HStack>
            )}

            <Stack
              mb={1}
              w={["100%", "100%", 600]}
              justifyContent="center"
              flexDirection="row"
              flexWrap="wrap"
            >
              <Button
                size="sm"
                mx={1}
                my={1}
                variant="solid"
                px={["3", "4", "4"]}
                bg={GlobalStyles.colors.white}
                borderColor={createBtnColor}
                borderWidth={2}
                borderRadius={40}
                _hover={{ bg: GlobalStyles.colors.grey }}
                onPress={() => changeTab("create")}
              >
                <Text
                  fontWeight="600"
                  fontFamily={GlobalStyles.text.fontFamily}
                  fontSize={["12px", "13px", "14px"]}
                  color={createBtnColor}
                >
                  Create Coupon
                </Text>
              </Button>
              <Button
                size="sm"
                mx={1}
                my={1}
                variant="solid"
                px={["3", "4", "4"]}
                bg={GlobalStyles.colors.white}
                borderColor={redeemBtnColor}
                borderWidth={2}
                borderRadius={40}
                _hover={{ bg: GlobalStyles.colors.grey }}
                onPress={() => changeTab("redeem")}
              >
                <Text
                  fontWeight="600"
                  fontFamily={GlobalStyles.text.fontFamily}
                  fontSize={["12px", "13px", "14px"]}
                  color={redeemBtnColor}
                >
                  Redeem Coupon
                </Text>
              </Button>

              <Button
                size="sm"
                mx={1}
                my={1}
                variant="solid"
                px={["3", "4", "4"]}
                bg={GlobalStyles.colors.white}
                borderColor={listBtnColor}
                borderWidth={2}
                borderRadius={40}
                _hover={{ bg: GlobalStyles.colors.grey }}
                onPress={() => changeTab("list")}
              >
                <Text
                  fontWeight="600"
                  fontFamily={GlobalStyles.text.fontFamily}
                  fontSize={["12px", "13px", "14px"]}
                  color={listBtnColor}
                >
                  Coupon List
                </Text>
              </Button>

              <Button
                size="sm"
                mx={1}
                my={1}
                variant="solid"
                px={["3", "4", "4"]}
                bg={GlobalStyles.colors.white}
                borderColor={analyticsBtnColor}
                borderWidth={2}
                borderRadius={40}
                _hover={{ bg: GlobalStyles.colors.grey }}
                onPress={() => changeTab("analytics")}
              >
                <Text
                  fontWeight="600"
                  fontFamily={GlobalStyles.text.fontFamily}
                  fontSize={["12px", "13px", "14px"]}
                  color={analyticsBtnColor}
                >
                  Analytics
                </Text>
              </Button>
            </Stack>

            {currentTab === "create" && formStep === "form" && (
              <CouponCreate onGenerated={addCoupon} />
            )}

            {currentTab === "create" && formStep === "generated" && (
              <VStack space={4} alignItems="center">
                <CouponGenerated coupon={newCoupon} />
                <Pressable onPress={() => setFormStep("form")}>
                  <Text
                    color={GlobalStyles.colors.blue}
                    fontWeight="600"
                    fontSize={["14px", "15px", "16px"]}
                    textDecoration="underline"
                    fontFamily={GlobalStyles.text.fontFamily}
                  >
                    Create Another Coupon
                  </Text>
                </Pressable>
              </VStack>
            )}

            {currentTab === "redeem" && <CouponRedemption />}

            {currentTab === "list" && (
              <CouponList
                coupons={couponsList}
                onDeleteSuccess={removeCoupon}
                onUpdateSuccess={updateCoupon}
              />
            )}
            {currentTab === "analytics" && (
              <CouponAnalytics coupons={couponsList} />
            )}
          </Stack>
        </Center>
      </ScrollView>

      {showScrollBtn && (
        <IconButton
          position="absolute"
          bottom={10}
          right={10}
          borderRadius="full"
          bg={GlobalStyles.colors.blue}
          _pressed={{ bg: "gray.500" }}
          icon={<ChevronUpIcon color="white" size={6} />}
          onPress={goToTop}
        />
      )}
    </>
  );
}